// Source / attribution capture (contract v1.2).
//
// Records WHERE the widget was embedded and HOW the visitor arrived, so firm
// staff can attribute a lead to a site / page / campaign. Captured client-side
// at widget load and sent with /chat/session.
//
// Privacy: query strings can carry identifiers, so we STRIP any param whose key
// matches a sensitive pattern (token/password/key/secret/auth) from url +
// referrer before sending, and cap lengths to the contract maxima. UTM params
// are read from the ORIGINAL query (they never match the sensitive pattern).

export interface SourceInfo {
  url?: string;
  origin?: string;
  path?: string;
  referrer?: string;
  page_title?: string;
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  };
  embedded_at?: string;
}

/** Overrides from data-source-* embed attributes (multi-domain setups, etc.). */
export interface SourceOverrides {
  url?: string;
  origin?: string;
  path?: string;
  referrer?: string;
  page_title?: string;
}

// Timestamp at SCRIPT LOAD (module eval), not session start — "when the widget
// appeared on the page".
const WIDGET_EMBEDDED_AT = new Date().toISOString();

const SENSITIVE_PARAM = /token|password|key|secret|auth/i;
const cap = (s: string | undefined, n: number): string | undefined =>
  s == null ? undefined : s.length > n ? s.slice(0, n) : s;
const clean = (s: string | undefined): string | undefined => {
  const t = (s ?? '').trim();
  return t ? t : undefined;
};

/** Remove sensitive query params from an absolute URL, then cap to `max`.
 * Returns undefined if the input isn't a parseable absolute URL (so a junk
 * value never fails the receiver's z.string().url() validation). */
function sanitizeUrl(raw: string | undefined, max: number): string | undefined {
  const v = clean(raw);
  if (!v) return undefined;
  try {
    const u = new URL(v);
    for (const k of [...u.searchParams.keys()]) {
      if (SENSITIVE_PARAM.test(k)) u.searchParams.delete(k);
    }
    return cap(u.toString(), max);
  } catch {
    return undefined; // not an absolute URL → omit rather than send garbage
  }
}

/** Validate a URL-ish value and return it AS-IS (capped) — no normalization, so
 * a bare origin keeps its exact form (no toString() trailing slash). Omits a
 * non-parseable value. Used for `origin` (no query params to strip). */
function validatedUrl(raw: string | undefined, max: number): string | undefined {
  const v = clean(raw);
  if (!v) return undefined;
  try {
    new URL(v);
    return cap(v, max);
  } catch {
    return undefined;
  }
}

function parseUtm(search: string): SourceInfo['utm'] | undefined {
  const p = new URLSearchParams(search);
  const utm = {
    source: cap(clean(p.get('utm_source') ?? undefined), 100),
    medium: cap(clean(p.get('utm_medium') ?? undefined), 100),
    campaign: cap(clean(p.get('utm_campaign') ?? undefined), 100),
    content: cap(clean(p.get('utm_content') ?? undefined), 100),
    term: cap(clean(p.get('utm_term') ?? undefined), 100),
  };
  // Only return the object if at least one UTM param is present.
  return Object.values(utm).some(Boolean) ? utm : undefined;
}

/**
 * Capture source info from the current page. `overrides` (from data-source-*)
 * win over the auto-detected values. Pure read of window/document — never
 * throws. Returns undefined only if there's nothing to report.
 */
export function captureSource(overrides: SourceOverrides = {}): SourceInfo | undefined {
  if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
  const loc = window.location;

  const url = sanitizeUrl(overrides.url ?? loc.href, 500);
  const origin = validatedUrl(overrides.origin ?? loc.origin, 200);
  const path = cap(clean(overrides.path ?? loc.pathname), 200);
  const referrer = sanitizeUrl(overrides.referrer ?? document.referrer, 500);
  const page_title = cap(clean(overrides.page_title ?? document.title), 200);
  const utm = parseUtm(loc.search);

  const src: SourceInfo = {
    url,
    origin,
    path,
    referrer,
    page_title,
    utm,
    embedded_at: WIDGET_EMBEDDED_AT,
  };
  // Drop undefined keys so the payload stays clean.
  (Object.keys(src) as (keyof SourceInfo)[]).forEach((k) => src[k] === undefined && delete src[k]);
  return Object.keys(src).length ? src : undefined;
}

/** Pull data-source-* overrides off the embed <script> dataset. */
export function sourceOverridesFromDataset(ds: DOMStringMap): SourceOverrides {
  return {
    url: ds.sourceUrl,
    origin: ds.sourceOrigin,
    path: ds.sourcePath,
    referrer: ds.sourceReferrer,
    page_title: ds.sourceTitle,
  };
}
